import { EventTypes } from '@candulabs/craft';
import debounce from 'lodash/debounce';
import { MutableRefObject, useCallback, useEffect, useRef, useState } from 'react';

import { setNodeLabelVisibility } from 'craft/components/NodeLabels/NodeLabel';
import { setResizeHandleVisibility } from 'craft/components/Resize/ResizeBoundariesObserver';
import { useEditor } from 'craft/hooks';
import { scrollTooltipIntoView } from 'craft/resolvers/Hotspot/utils/useTooltipPosition';
import { useToolboxTab, useViewportDom } from 'craft/state';
import { getSelectorFromIframe } from 'craft/utils/snapshot-iframe';

const getPosition = (element: Element | null | undefined, positionRelativeTo: 'right' | 'left') => {
  if (!element) {
    return { top: 0, left: 0 };
  }

  const { top, left, width } = element.getBoundingClientRect();

  if (positionRelativeTo === 'right') {
    return { top, left: left + width };
  }
  return { top, left };
};

type BeaconPosition = { top: number; left: number };

interface Props {
  placementSelector: string | undefined;
  refs: Record<string, MutableRefObject<HTMLDivElement | undefined>>;
  recalculateTooltipPosition: VoidFunction;
  selectedNodes: string[] | undefined;
  nodeId: string;
  isBeaconActive: boolean;
  positionRelativeTo: 'right' | 'left';
}

export const useBeaconPosition = ({
  placementSelector,
  refs,
  recalculateTooltipPosition,
  selectedNodes,
  nodeId,
  isBeaconActive,
  positionRelativeTo,
}: Props): { beaconPosition: BeaconPosition; isBeaconVisible: boolean } => {
  const { actions } = useEditor();
  const { forcePlacementsTab } = useToolboxTab();

  const placementSelectorEl = getSelectorFromIframe(placementSelector);
  const { viewportWrapperEl } = useViewportDom();
  const [beaconPosition, setBeaconPosition] = useState<BeaconPosition>({ top: 0, left: 0 });
  const latestPosition = useRef<any>([]);

  // when in edit mode, hide all beacons except the active one
  const [isBeaconVisible, setIsBeaconVisible] = useState(true);

  const debouncedSetBeaconPosition = useCallback(
    debounce((value: BeaconPosition, isHotspotSelected: boolean) => {
      setBeaconPosition(value);

      // make sure to recalculate tooltip position every time the beacon position changes
      recalculateTooltipPosition();

      // show beacon, tooltip and arrow after new position is set
      if (refs.beacon.current) {
        // eslint-disable-next-line no-param-reassign
        refs.beacon.current.style.opacity = '1';
      }
      if (refs.wrapper.current) {
        // eslint-disable-next-line no-param-reassign
        refs.wrapper.current.style.opacity = '1';
      }
      if (refs.arrow.current) {
        // eslint-disable-next-line no-param-reassign
        refs.arrow.current.style.opacity = '1';
      }

      // show resize handles and node label if hotspot was selected before setting new position
      if (isHotspotSelected) {
        setResizeHandleVisibility(true);
        setNodeLabelVisibility(true);
      }
    }, 400),
    [recalculateTooltipPosition, refs],
  );

  const calculateSetPosition = useCallback(() => {
    const position = placementSelectorEl
      ? getPosition(placementSelectorEl, positionRelativeTo)
      : {
          top: (viewportWrapperEl?.offsetHeight || 500) / 2,
          left: (viewportWrapperEl?.offsetWidth || 500) / 2,
        };

    if (forcePlacementsTab) {
      setIsBeaconVisible(isBeaconActive && !!placementSelectorEl);
    } else {
      setIsBeaconVisible(true);
    }

    // don't do anything if the beacon position hasn't changed
    if (
      position.top === latestPosition.current.top &&
      position.left === latestPosition.current.left
    ) {
      return;
    }

    latestPosition.current = position;
    const isHotspotSelected = !!selectedNodes && selectedNodes.includes(nodeId);

    // hide resize handles and node label if hotspot is selected
    if (isHotspotSelected) {
      setResizeHandleVisibility(false);
      setNodeLabelVisibility(false);
    }

    // hide beacon, tooltip and arrow until new position is set
    if (refs.beacon.current) {
      // eslint-disable-next-line no-param-reassign
      refs.beacon.current.style.opacity = '0';
    }
    if (refs.wrapper.current) {
      // eslint-disable-next-line no-param-reassign
      refs.wrapper.current.style.opacity = '0';
    }
    if (refs.arrow.current) {
      // eslint-disable-next-line no-param-reassign
      refs.arrow.current.style.opacity = '0';
    }

    debouncedSetBeaconPosition(position, isHotspotSelected);
  }, [
    viewportWrapperEl,
    placementSelectorEl,
    refs.beacon.current,
    refs.wrapper.current,
    refs.arrow.current,
    latestPosition.current,
    forcePlacementsTab,
    isBeaconActive,
    positionRelativeTo,
  ]);

  useEffect(() => {
    if (!viewportWrapperEl) {
      return () => null;
    }

    // make sure to recalculate beacon position when the viewportContainer is resized
    const observer = new ResizeObserver(calculateSetPosition);
    observer.observe(viewportWrapperEl);

    return () => observer.disconnect();
  }, [viewportWrapperEl, calculateSetPosition]);

  useEffect(() => {
    const interval = setInterval(() => {
      calculateSetPosition();
    }, 20);

    return () => {
      clearInterval(interval);
    };
  }, [calculateSetPosition]);

  useEffect(() => {
    // when a beacon becomes active or its placement changes, make sure to scroll it into
    // the snapshot's view and select the node
    if (isBeaconActive) {
      // Scrolling the hotspot into view before expanding it gives us a more natural tooltip position
      scrollTooltipIntoView({ isActive: isBeaconActive, selector: placementSelector });
      actions.setEventNodes(EventTypes.selected, [nodeId]);
    }
  }, [isBeaconActive, nodeId, scrollTooltipIntoView, placementSelector]);

  return { beaconPosition, isBeaconVisible };
};
